// components/EligibilityModal.jsx
import React, { useState, useEffect } from 'react';
import Confetti from 'react-confetti';
import { useNavigate } from 'react-router-dom';
import '../../components/EligibilityModal.css';

const PmicpEligibilityModal = ({ isOpen, onClose, onComplete }) => {
  // Réinitialiser tous les états à leur valeur par défaut
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [showResults, setShowResults] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });

  // État pour les alertes personnalisées
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');

  const [formData, setFormData] = useState({
    // Étape 1: Niveau académique
    niveauAcademique: '',

    // Étape 2: Expérience (Oui/Non)
    experienceProjet: '',

    // Étape 3: QCMs PMI-CP (regroupés)
    qcm1: '',
    qcm2: '',
    qcm3: '',
    qcm4: ''
  });

  const [scoreQCM, setScoreQCM] = useState(0);
  const [directRecommendation, setDirectRecommendation] = useState(null); // Pour les recommandations directes (sans QCM)

  // Questions du questionnaire
  const questions = [
    {
      id: 1,
      title: "Niveau académique",
      description: "Quel est votre niveau académique le plus élevé ?",
      type: 'simple',
      field: {
        name: 'niveauAcademique',
        type: 'select',
        options: [
          { value: '', label: 'Sélectionnez votre niveau' },
          { value: 'inf_bac3', label: 'Inférieur à BAC+3 (BAC, BTS, DUT, Licence 1/2)' },
          { value: 'sup_bac3', label: 'Supérieur ou égal à BAC+3 (Licence 3, Master, Doctorat, Ingénieur)' }
        ],
        required: true
      }
    },
    {
      id: 2,
      title: "Expérience en gestion de projet",
      description: "Avez-vous au moins 3 ans d'expérience en gestion de projet ?",
      type: 'simple',
      field: {
        name: 'experienceProjet',
        type: 'radio',
        options: [
          { value: 'oui', label: 'Oui, j\'ai 3 ans ou plus d\'expérience' },
          { value: 'non', label: 'Non, j\'ai moins de 3 ans d\'expérience' }
        ],
        required: true
      }
    },
    {
      id: 3,
      title: "Questionnaire PMI-CP",
      description: "Répondez aux questions suivantes pour évaluer vos connaissances en gestion de projet :",
      type: 'qcm',
      questions: [
        {
          id: 'qcm1',
          text: "Dans le cadre d’un projet de construction, le client demande une modification du tracé initial, entraînant un ajustement des coûts et des délais. Cette modification est formellement approuvée par les deux parties. Par ailleurs, l’entreprise soumet une demande suite à des conditions imprévues du sol non identifiées initialement. Quelle est la principale différence entre un ordre de modification et une réclamation ?",
          options: [
            { value: 'a', label: "A. Les réclamations ne sont déposées qu'à l'expiration du contrat, tandis que les ordres de modification interviennent en cours de projet", points: 0 },
            { value: 'b', label: "B. Les ordres de modification ajustent les termes du contrat, tandis que les réclamations visent à obtenir une indemnisation pour des manquements ou des pertes présumés ", points: 25 },
            { value: 'c', label: "C. Les ordres de modification résultent de désaccords, tandis que les réclamations font toujours l'objet d'un accord", points: 0 },
            { value: 'd', label: "D. Les réclamations modifient le périmètre, tandis que les ordres de modification ne concernent que les prolongations de délai", points: 0 }
          ],
          correctAnswer: 'b',
          explanation: ""
        },
        {
          id: 'qcm2',
          text: "Lors d'une récente réunion avec les parties prenantes, plusieurs intervenants ont fait part de leurs préoccupations concernant le calendrier du projet. Comment devriez-vous réagir ?",
          options: [
            { value: 'a', label: "A. Reconnaitre leurs inquiétudes et proposer un plan d'actions pour résoudre les problèmes liés au calendrier", points: 25 },
            { value: 'b', label: "B. Ignorer leurs inquiétudes pour maintenir le cap de la réunion", points: 0 },
            { value: 'c', label: "C. Organiser une autre réunion pour aborder ces préoccupations séparément", points: 0 },
            { value: 'd', label: "D. Donner un aperçu général du calendrier sans entrer dans les détails", points: 0 }
          ],
          correctAnswer: 'a',
          explanation: ""
        },
        {
          id: 'qcm3',
          text: "Un projet connait une extention du périmètre inital. En tant que profesionnel de la contruction, vous décidez de mettre en œuvre une approche d'ingénierie de la valeur. Quel objectif recherchez vous en appliquant cette technique ?",
          options: [
            { value: 'a', label: "A. Éliminer les éléments non essentiels du projet afin de réduire les coûts sans consulter les parties prenantes", points: 0 },
            { value: 'b', label: "B. Élargir encore davantage la portée du projet en ajoutant des fonctionnalités supplémentaires souhaitées par les parties prenantes", points: 0 },
            { value: 'c', label: "C. Améliorer la valeur du projet en analysant les fonctions et en identifiant les possibilités de réduction des coûts tout en maintenant les performances", points: 25 },
            { value: 'd', label: "D. Établir un nouveau budget reflétant le nouveau périmètre sans analyser l'impact sur les résultats du projet", points: 0 }
          ],
          correctAnswer: 'c',
          explanation: ""
        },
        {
          id: 'qcm4',
          text: "Vous évaluez un nouvel outil logiciel destiné à gérer le périmètre et les ordres de modification. Potentiellement, quel inconvénient devriez-vous prendre en compte concernant l'utilisation de la technologie dans ce processus ?",
          options: [
            { value: 'a', label: "A. Une transparence et une communication accrues.", points: 0 },
            { value: 'b', label: "B. Une documentation et un suivi efficace.", points: 0 },
            { value: 'c', label: "C. Des capacités d'analyse des données améliorées.", points: 0 },
            { value: 'd', label: "D. Le risque que la technologie complique le processus de gestion des modifications si elle n'est pas utilisée correctement.", points: 25 }
          ],
          correctAnswer: 'd',
          explanation: ""
        }
      ]
    }
  ];

  // Effet pour réinitialiser complètement quand le modal est fermé
  useEffect(() => {
    if (!isOpen) {
      // Réinitialiser tous les états à zéro
      setCurrentStep(1);
      setShowResults(false);
      setShowConfetti(false);
      setDirectRecommendation(null);
      setFormData({
        niveauAcademique: '',
        experienceProjet: '',
        qcm1: '',
        qcm2: '',
        qcm3: '',
        qcm4: ''
      });
      setScoreQCM(0);
      setShowAlert(false);
      setAlertMessage('');
    }
  }, [isOpen]);

  // Effet pour gérer la taille de la fenêtre
  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Effet pour arrêter les confettis après 5 secondes
  useEffect(() => {
    if (showConfetti) {
      const timer = setTimeout(() => {
        setShowConfetti(false);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [showConfetti]);

  // Effet pour fermer automatiquement l'alerte après 3 secondes
  useEffect(() => {
    if (showAlert) {
      const timer = setTimeout(() => {
        setShowAlert(false);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [showAlert]);

  // Fonction pour afficher une alerte personnalisée
  const showCustomAlert = (message) => {
    setAlertMessage(message);
    setShowAlert(true);
  };

  // Fonction pour fermer le modal et réinitialiser
  const handleCloseModal = () => {
    onClose();
  };

  // Calculer le score QCM
  const calculateQCMScore = () => {
    let score = 0;

    if (formData.qcm1 === 'b') score += 25;
    if (formData.qcm2 === 'a') score += 25;
    if (formData.qcm3 === 'c') score += 25;
    if (formData.qcm4 === 'd') score += 25;

    setScoreQCM(score);
    return score;
  };

  // Vérifier si toutes les questions QCM sont répondues
  const areAllQCMsAnswered = () => {
    return formData.qcm1 && formData.qcm2 && formData.qcm3 && formData.qcm4;
  };

  // Déterminer la recommandation finale (après QCM)
  const getRecommendation = () => {
    const niveau = formData.niveauAcademique;
    const experience = formData.experienceProjet;

    // Règles de recommandation
    if (niveau === 'inf_bac3' && experience === 'oui') {
      return {
        certification: 'PMI-CP',
        reason: 'Nous vous recommandons la certification PMI-CP. Bien que votre niveau académique soit inférieur à BAC+3, votre expérience de 3 ans ou plus vous rend éligible pour cette certification.'
      };
    }

    if (niveau === 'inf_bac3') {
      return {
        certification: 'CAPM',
        reason: 'Nous vous recommandons la certification CAPM. Votre niveau académique (inférieur à BAC+3) correspond parfaitement à cette formation d\'introduction à la gestion de projet.'
      };
    }

    if (niveau === 'sup_bac3' && experience === 'non') {
      return {
        certification: 'CAPM',
        reason: 'Nous vous recommandons la certification CAPM. Avec un niveau BAC+3 ou plus mais moins de 3 ans d\'expérience, cette certification vous permettra de solidifier vos bases avant de passer au PMI-CP.'
      };
    }

    return {
      certification: 'PMI-CP',
      reason: 'Nous vous recommandons la certification PMI-CP. Votre profil (BAC+3 ou plus avec 3 ans ou plus d\'expérience) correspond aux critères d\'éligibilité.'
    };
  };

  // Déterminer la recommandation directe (sans QCM)
  const getDirectRecommendation = () => {
    const niveau = formData.niveauAcademique;
    const experience = formData.experienceProjet;

    // Si réponse NON à l'expérience (moins de 3 ans) → CAPM direct
    if (experience === 'non') {
      return {
        certification: 'CAPM',
        reason: 'Avec moins de 3 ans d\'expérience en gestion de projet, nous vous recommandons la certification CAPM. Cette formation vous permettra d\'acquérir les bases fondamentales de la gestion de projet et de vous préparer pour le PMI-CP dans le futur.'
      };
    }

    // Si réponse OUI à l'expérience (3 ans ou plus) → on vérifie le niveau académique
    if (experience === 'oui') {
      // Niveau inférieur à BAC+3 mais avec expérience 3+ ans → PMI-CP
      if (niveau === 'inf_bac3') {
        return {
          certification: 'PMI-CP',
          reason: 'Bien que votre niveau académique soit inférieur à BAC+3, votre expérience de 3 ans ou plus vous rend éligible pour la certification PMI-CP. Nous vous proposons de passer le questionnaire pour évaluer vos connaissances.'
        };
      }
      // Niveau BAC+3+ et expérience 3+ ans → PMI-CP avec QCM
      return {
        certification: 'PMI-CP',
        reason: 'Votre profil (BAC+3 ou plus avec 3 ans ou plus d\'expérience) vous rend éligible pour la certification PMI-CP. Nous vous proposons de passer le questionnaire pour évaluer vos connaissances.'
      };
    }

    return null;
  };

  // Évaluer le score QCM
  const getQCMEvaluation = (score) => {
    if (score === 0) return { niveau: 'Faible', couleur: '#ef4444' };
    if (score <= 25) return { niveau: 'Faible', couleur: '#ef4444' };
    if (score <= 50) return { niveau: 'Passable', couleur: '#f59e0b' };
    if (score <= 75) return { niveau: 'Assez bien', couleur: '#10b981' };
    return { niveau: 'Très bien', couleur: '#10b981' };
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleNext = () => {
    // Validation de l'étape courante
    const currentQuestion = questions[currentStep - 1];

    if (currentQuestion.type === 'simple') {
      if (!formData[currentQuestion.field.name]) {
        showCustomAlert('Veuillez sélectionner une réponse avant de continuer.');
        return;
      }
    }

    // Si on est à l'étape 2 (expérience) et qu'on clique sur Suivant
    if (currentStep === 2) {
      const experienceValue = formData.experienceProjet;

      // Si la réponse est "non" (moins de 3 ans d'expérience)
      if (experienceValue === 'non') {
        // Redirection directe vers CAPM sans QCM
        const directReco = {
          certification: 'CAPM',
          reason: 'Avec moins de 3 ans d\'expérience en gestion de projet, nous vous recommandons la certification CAPM. Cette formation vous permettra d\'acquérir les bases fondamentales de la gestion de projet et de vous préparer pour le PMI-CP dans le futur.'
        };

        setDirectRecommendation(directReco);
        setShowResults(true);
        setShowConfetti(true);

        // Enregistrer dans localStorage
        const scoreData = {
          score: 0,
          niveau: 'Non applicable',
          date: new Date().toISOString(),
          recommendation: 'CAPM',
          formData: formData,
          directRecommendation: true
        };

        localStorage.setItem('pmicp_eligibility_score', JSON.stringify(scoreData));
        return;
      }

      // Si la réponse est "oui" (3 ans ou plus), on continue vers l'étape 3 (QCM)
      if (experienceValue === 'oui') {
        setCurrentStep(prev => prev + 1);
        return;
      }
    }

    // Pour les autres étapes, comportement normal
    if (currentStep < questions.length) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleFinish = () => {
    // Vérifier que toutes les questions QCM sont répondues
    if (!areAllQCMsAnswered()) {
      showCustomAlert('Veuillez répondre à toutes les questions avant de terminer.');
      return;
    }

    // Calculer le score et afficher les résultats
    const calculatedScore = calculateQCMScore();
    setShowResults(true);
    setShowConfetti(true);

    // Enregistrer les données dans localStorage
    const recommendation = getRecommendation();
    const qcmEvaluation = getQCMEvaluation(calculatedScore);

    const scoreData = {
      score: calculatedScore,
      niveau: qcmEvaluation.niveau,
      date: new Date().toISOString(),
      recommendation: recommendation.certification,
      formData: formData,
      directRecommendation: false
    };

    localStorage.setItem('pmicp_eligibility_score', JSON.stringify(scoreData));
  };

  const handleSubmit = () => {
    let recommendation;

    // Si c'est une recommandation directe (sans QCM)
    if (directRecommendation) {
      recommendation = directRecommendation;
    } else {
      recommendation = getRecommendation();
    }

    const qcmEvaluation = getQCMEvaluation(scoreQCM);

    if (onComplete) {
      onComplete({
        ...formData,
        scoreQCM,
        qcmEvaluation,
        recommendation
      });
    }

    // 🔥 VÉRIFIER S'IL Y A UNE SESSION CALENDRIER EN ATTENTE
    const pendingReference = localStorage.getItem('pending_calendar_reference');
    const pendingSession = localStorage.getItem('pending_calendar_session');
    const pendingCertification = localStorage.getItem('pending_calendar_certification');

    // Fermer la modal
    handleCloseModal();

    // // Scroll vers la section appropriée
    // if (recommendation.certification === 'PMI-CP') {
    //   setTimeout(() => {
    //     const calendarSection = document.getElementById('calendrier');
    //     if (calendarSection) {
    //       calendarSection.scrollIntoView({ 
    //         behavior: 'smooth',
    //         block: 'start'
    //       });
    //     } else {
    //       window.scrollTo({
    //         top: document.body.scrollHeight * 0.6,
    //         behavior: 'smooth'
    //       });
    //     }
    //   }, 300);
    // }

    if (pendingReference && pendingSession) {
      // 🔥 REDIRIGER VERS L'INSCRIPTION AVEC LES DONNÉES DU CALENDRIER
      try {
        const sessionData = JSON.parse(pendingSession);

        // Nettoyer le localStorage
        localStorage.removeItem('pending_calendar_session');
        localStorage.removeItem('pending_calendar_reference');
        localStorage.removeItem('pending_calendar_certification');

        // Rediriger vers l'inscription
        setTimeout(() => {
          navigate('/inscription', {
            state: {
              formationReference: pendingReference,
              sessionData: sessionData,
              eligibilityCompleted: true,
              certificationType: pendingCertification || "PMICP",
              fromCalendar: true
            }
          });
        }, 300);
        return;
      } catch (error) {
        console.error("Erreur lors de la lecture de la session:", error);
        // Nettoyer en cas d'erreur
        localStorage.removeItem('pending_calendar_session');
        localStorage.removeItem('pending_calendar_reference');
        localStorage.removeItem('pending_calendar_certification');
      }
    }

    if (recommendation.certification === 'CAPM') {
      setTimeout(() => {
        const contactSection = document.getElementById('contact');
        if (contactSection) {
          contactSection.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        } else {
          window.scrollTo({
            top: document.body.scrollHeight,
            behavior: 'smooth'
          });
        }
      }, 300);
    }
  };

  const isLastStep = currentStep === questions.length;
  const currentQuestion = questions[currentStep - 1];
  const recommendation = directRecommendation || getRecommendation();
  const qcmEvaluation = getQCMEvaluation(scoreQCM);
  const allQCMsAnswered = areAllQCMsAnswered();

  if (!isOpen) return null;

  return (
    <>
      {/* Alerte personnalisée */}
      {showAlert && (
        <div className="custom-alert">
          <div className="alert-content">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="12" fill="#f59e0b" />
              <path d="M12 8V12M12 16H12.01" stroke="white" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <span>{alertMessage}</span>
          </div>
        </div>
      )}

      {/* Confettis */}
      {showConfetti && (
        <Confetti
          width={windowSize.width}
          height={windowSize.height}
          recycle={true}
          numberOfPieces={200}
          gravity={0.1}
          colors={['#0a3764', '#94bf3e', '#10b981', '#f59e0b', '#ef4444']}
          style={{ position: 'fixed', zIndex: 1001 }}
        />
      )}

      <div className="eligibility-modal-overlay">
        <div className="eligibility-modal">
          {/* En-tête de la modal */}
          <div className="modal-header">
            <div className="header-content">
              <h2 className="modal-title">Questionnaire d'Éligibilité</h2>
            </div>

            <button className="close-btn" onClick={handleCloseModal}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>

          {/* Contenu de la modal */}
          <div className="modal-content">
            {!showResults ? (
              <>
                <div className="question-header">
                  <h3>{currentQuestion.description}</h3>
                </div>

                <div className="form-fields">
                  {currentQuestion.type === 'simple' ? (
                    currentQuestion.field.type === 'radio' ? (
                      // Affichage des boutons radio pour l'étape 2
                      <div className="radio-group">
                        {currentQuestion.field.options.map((option, optIndex) => (
                          <div key={optIndex} className="radio-option">
                            <input
                              type="radio"
                              id={`${currentQuestion.field.name}_${option.value}`}
                              name={currentQuestion.field.name}
                              value={option.value}
                              checked={formData[currentQuestion.field.name] === option.value}
                              onChange={handleChange}
                              className="radio-input"
                            />
                            <label
                              htmlFor={`${currentQuestion.field.name}_${option.value}`}
                              className="radio-label"
                            >
                              {option.label}
                            </label>
                          </div>
                        ))}
                      </div>
                    ) : (
                      // Affichage du select pour l'étape 1
                      <div className="form-field">
                        <label className="field-label">
                          Sélectionnez votre réponse
                          <span className="required">*</span>
                        </label>

                        <select
                          name={currentQuestion.field.name}
                          value={formData[currentQuestion.field.name] || ''}
                          onChange={handleChange}
                          className="form-input"
                          required={currentQuestion.field.required}
                        >
                          {currentQuestion.field.options.map((option, optIndex) => (
                            <option key={optIndex} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    )
                  ) : (
                    // QCMs regroupés (étape 3)
                    <div className="qcm-container">
                      {currentQuestion.questions.map((qcm, qcmIndex) => (
                        <div key={qcm.id} className="qcm-group">
                          <div className="qcm-header">
                            <h4 className="qcm-title">{qcmIndex + 1}.</h4>
                            <p className="qcm-text">{qcm.text}</p>
                          </div>

                          <div className="qcm-options">
                            {qcm.options.map((option, optIndex) => (
                              <div key={optIndex} className="qcm-option">
                                <input
                                  type="radio"
                                  id={`${qcm.id}_${option.value}`}
                                  name={qcm.id}
                                  value={option.value}
                                  checked={formData[qcm.id] === option.value}
                                  onChange={handleChange}
                                  className="qcm-radio"
                                />
                                <label
                                  htmlFor={`${qcm.id}_${option.value}`}
                                  className="qcm-label"
                                >
                                  <span className="qcm-text">{option.label}</span>
                                </label>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            ) : (
              // Résultats de l'évaluation
              <>
                <div className="eligibility-results">
                  {/* Score QCM (si applicable) */}
                  {!directRecommendation && (
                    <div className="score-card">
                      <div className="score-header">
                        <h4>Score</h4>
                        <div className="score-badge" style={{ backgroundColor: qcmEvaluation.couleur }}>
                          {scoreQCM}/100
                        </div>
                      </div>
                      <p className="score-description">
                        Niveau : <strong>{qcmEvaluation.niveau}</strong>
                      </p>

                      <div className="score-bar-container">
                        <div className="score-bar">
                          <div
                            className="score-fill"
                            style={{
                              width: `${scoreQCM}%`,
                              backgroundColor: qcmEvaluation.couleur
                            }}
                          ></div>
                        </div>
                        <div className="score-legend">
                          <span>0</span>
                          <span>25 (Faible)</span>
                          <span>50 (Passable)</span>
                          <span>75 (Assez bien)</span>
                          <span>100 (Très bien)</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Message de recommandation */}
                  <div className="text-center mt-4">
                    <div className="mb-3">
                      <svg width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="12" fill="#10b981" />
                        <path d="M10 15L7 12L5.5 13.5L10 18L18.5 9.5L17 8L10 15Z" fill="white" />
                      </svg>
                    </div>

                    <div className="recommendation-message">
                      <p style={{
                        color: '#4b5563',
                        fontSize: '0.95rem',
                        lineHeight: '1.5',
                        margin: '0'
                      }}>
                        {recommendation.reason}
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Footer de la modal avec les boutons */}
          <div className="modal-footer">
            <div className="footer-actions">
              {!showResults ? (
                <div className="actions-container">
                  {currentStep > 1 ? (
                    <>
                      <button className="btn-secondary" onClick={handlePrev}>
                        Précédent
                      </button>

                      <button
                        className={`${isLastStep && allQCMsAnswered ? 'btn-success' : 'btn-primary'}`}
                        onClick={isLastStep ? handleFinish : handleNext}
                        disabled={isLastStep && !allQCMsAnswered}
                      >
                        {isLastStep ? 'Terminer' : 'Suivant'}
                      </button>
                    </>
                  ) : (
                    <div className="step1-actions">
                      <button
                        className="btn-primary"
                        onClick={handleNext}
                      >
                        Suivant
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="actions-container" style={{ justifyContent: 'center' }}>
                  <button className="btn-success" onClick={handleSubmit} style={{ margin: '0 auto' }}>
                    {recommendation.certification === 'PMI-CP'
                      ? "S'inscrire à la prochaine session PMI-CP"
                      : 'Découvrir la formation CAPM'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <style jsx>{`
          /* Alerte personnalisée */
          .custom-alert {
            position: fixed;
            top: 20px;
            right: 20px;
            background: #f59e0b;
            color: white;
            padding: 12px 18px;
            border-radius: 8px;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
            z-index: 1002;
            animation: slideIn 0.3s ease-out;
            max-width: 400px;
            min-width: 300px;
          }
          
          @keyframes slideIn {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
          
          .alert-content {
            display: flex;
            align-items: center;
            gap: 12px;
          }
          
          .alert-content svg {
            flex-shrink: 0;
          }
          
          .alert-content span {
            font-size: 0.95rem;
            font-weight: 500;
            line-height: 1.4;
          }

          /* Overlay */
          .eligibility-modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0, 0, 0, 0.5);
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 1000;
            padding: 18px;
          }

          /* Modal principale */
          .eligibility-modal {
            background: white;
            border-radius: 10px;
            width: 100%;
            max-width: 800px;
            max-height: 95vh;
            display: flex;
            flex-direction: column;
            box-shadow: 0 18px 54px rgba(0, 0, 0, 0.25);
            overflow: hidden;
          }

          /* En-tête */
          .modal-header {
            background: linear-gradient(135deg, #0a3764 0%, #09407e 100%);
            color: white;
            padding: 1.3rem 1.8rem;
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            position: relative;
          }

          .header-content {
            flex: 1;
            display: flex;
            align-items: center;
          }

          .modal-title {
            margin: 0;
            font-size: 1.6rem;
            line-height: 1.2;
          }

          .close-btn {
            background: none;
            border: none;
            color: white;
            cursor: pointer;
            padding: 0.4rem;
            border-radius: 3px;
            transition: background 0.2s;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
          }

          .close-btn:hover {
            background: rgba(255, 255, 255, 0.1);
          }

          .close-btn svg {
            width: 20px;
            height: 20px;
          }

          /* Contenu */
          .modal-content {
            flex: 1;
            overflow-y: auto;
            padding: 0.8rem 1.8rem;
            scrollbar-width: thin;
            scrollbar-color: #c1c1c1 #f1f1f1;
          }

          .modal-content::-webkit-scrollbar {
            width: 6px;
          }

          .modal-content::-webkit-scrollbar-track {
            background: #f1f1f6;
            border-radius: 3px;
          }

          .modal-content::-webkit-scrollbar-thumb {
            background: #c1c1c1;
            border-radius: 3px;
          }

          .modal-content::-webkit-scrollbar-thumb:hover {
            background: #a8a8a8;
          }

          /* Question header */
          .question-header {
            margin-bottom: 1.8rem;
          }

          .question-header h3 {
            color: #0a3764;
            margin-bottom: 0.4rem;
            font-size: 1.3rem;
            text-align: left;
          }

          /* Radio group */
          .radio-group {
            display: flex;
            flex-direction: column;
            gap: 1rem;
          }

          .radio-option {
            display: flex;
            align-items: center;
            padding: 1rem;
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            transition: all 0.2s;
            cursor: pointer;
          }

          .radio-option:hover {
            border-color: #0a3764;
            background: #f0f7ff;
          }

          .radio-input {
            margin-right: 0.75rem;
            width: 18px;
            height: 18px;
            cursor: pointer;
          }

          .radio-label {
            cursor: pointer;
            font-size: 0.95rem;
            color: #374151;
            flex: 1;
          }

          /* QCM Container */
          .qcm-container {
            display: flex;
            flex-direction: column;
            gap: 1.8rem;
          }

          .qcm-header {
            display: flex;
            align-items: flex-start;
            gap: 0.4rem;
            margin-bottom: 0.8rem;
          }

          .qcm-title {
            color: #0a3764;
            font-size: 1.1rem;
            margin: 0;
            line-height: 1.4;
            min-width: 1.3rem;
            flex-shrink: 0;
          }

          .qcm-text {
            color: #666;
            font-size: 0.95rem;
            line-height: 1.4;
            margin: 0;
            flex: 1;
          }

          .qcm-options {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 0.8rem;
          }

          .qcm-option {
            display: flex;
            align-items: flex-start;
            padding: 0.8rem;
            border: 1px solid #e5e7eb;
            border-radius: 6px;
            transition: all 0.2s;
            cursor: pointer;
            height: 100%;
          }

          .qcm-option:hover {
            border-color: #0a3764;
            background: #f0f7ff;
          }

          .qcm-radio {
            margin-right: 0.75rem;
            width: 18px;
            height: 18px;
            margin-top: 0.125rem;
            flex-shrink: 0;
          }

          .qcm-label {
            display: flex;
            align-items: flex-start;
            cursor: pointer;
            width: 100%;
            height: 100%;
          }

          /* Form fields */
          .form-field {
            margin-bottom: 1.5rem;
          }

          .field-label {
            display: block;
            margin-bottom: 0.5rem;
            color: #374151;
            font-weight: 500;
            font-size: 0.9rem;
          }

          .required {
            color: #ef4444;
            margin-left: 2px;
          }

          .form-input {
            width: 100%;
            padding: 0.7rem 0.9rem;
            border: 1px solid #d1d5db;
            border-radius: 5px;
            font-size: 0.9rem;
            transition: border-color 0.2s;
          }

          .form-input:focus {
            outline: none;
            border-color: #0a3764;
            box-shadow: 0 0 0 2px rgba(10, 55, 100, 0.1);
          }

          /* Footer */
          .modal-footer {
            background: #f9fafb;
            border-top: 1px solid #e5e7eb;
            padding: 1rem 1.8rem;
          }

          .footer-actions {
            width: 100%;
          }

          .actions-container {
            display: flex;
            justify-content: space-between;
            align-items: center;
            width: 100%;
            gap: 0.8rem;
          }

          .step1-actions {
            display: flex;
            justify-content: flex-end;
            width: 100%;
          }

          /* Score Card */
          .score-card {
            background: white;
            border-radius: 8px;
            padding: 1.3rem;
          }

          .score-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 0.8rem;
          }

          .score-header h4 {
            font-size: 1.1rem;
            color: #0a3764;
            margin: 0;
          }

          .score-badge {
            padding: 0.4rem 0.9rem;
            border-radius: 18px;
            color: white;
            font-weight: bold;
            font-size: 0.95rem;
          }

          .score-description {
            margin-bottom: 1.3rem;
            color: #4b5563;
            font-size: 0.9rem;
          }

          .score-bar-container {
            margin-bottom: 1.3rem;
          }

          .score-bar {
            height: 10px;
            background: #f3f4f6;
            border-radius: 5px;
            overflow: hidden;
            margin-bottom: 0.4rem;
          }

          .score-fill {
            height: 100%;
            border-radius: 5px;
            transition: width 0.3s ease;
          }

          .score-legend {
            display: flex;
            justify-content: space-between;
            font-size: 0.7rem;
            color: #6b7280;
          }

          /* Boutons */
          .btn-primary, .btn-secondary, .btn-success {
            padding: 0.7rem 1.3rem;
            border-radius: 5px;
            font-weight: 600;
            font-size: 0.9rem;
            cursor: pointer;
            border: none;
            transition: all 0.2s;
            min-width: 118px;
            width: auto;
          }

          .btn-primary {
            background: #0a3764;
            color: white;
          }

          .btn-primary:hover:not(:disabled) {
            background: #09407e;
            transform: translateY(-1px);
          }

          .btn-secondary {
            background: #6b7280;
            color: white;
          }

          .btn-secondary:hover {
            background: #4b5563;
            transform: translateY(-1px);
          }

          .btn-success {
            background: #94bf3e;
            color: white;
          }

          .btn-success:hover {
            background: #83a935;
            transform: translateY(-1px);
          }

          .btn-primary:disabled,
          .btn-success:disabled {
            background: #d1d5db;
            color: #6b7280;
            cursor: not-allowed;
            opacity: 0.6;
            transform: none;
          }

          .text-center {
            text-align: center !important;
          }

          .mt-4 {
            margin-top: 1.5rem !important;
          }

          .mb-3 {
            margin-bottom: 1rem !important;
          }

          .recommendation-message {
            max-width: 800px;
            margin: 0 auto;
            padding: 0 1rem;
          }

          /* Responsive */
          @media (max-width: 768px) {
            .custom-alert {
              top: 10px;
              right: 10px;
              left: 10px;
              max-width: none;
              min-width: auto;
            }
            
            .modal-header {
              padding: 1rem 1.2rem;
            }
            
            .modal-title {
              font-size: 1.3rem;
            }
            
            .modal-content {
              padding: 1rem 1.2rem;
            }
            
            .qcm-options {
              grid-template-columns: 1fr;
              gap: 0.6rem;
            }
            
            .actions-container {
              flex-direction: column;
            }
            
            .step1-actions {
              justify-content: center;
            }
            
            .btn-primary, .btn-secondary, .btn-success {
              width: 100%;
              padding: 0.8rem 1rem;
            }
          }
        `}</style>
      </div>
    </>
  );
};

export default PmicpEligibilityModal;